// import api's and mutations
import { getDutyLinesSummary } from 'RestAPIs/admin';

// import atom
import { yearDateRangeAtom } from '../atoms';

// import components
import { Box } from '@mui/material';
import YearPeriodSelector from './YearPeriodSelector';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';

// import constants
import { GRAY_SECONDARY } from 'constants/colors';

// import hooks
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useSelector } from 'hooks';
import { useRecoilState } from 'recoil';

// import styles
import { StyledTableBoxWrapper } from 'components/shared/Box/Box.styles';

// import utils
import { endOfYear, startOfYear } from 'date-fns';
import { getLocalizedMonthName, toDateString } from 'utils/date';
import { CustomGridTreeDataGroupingCell } from './DutyLineSummaryGrid.components';
import { toMonth } from './DutyLineSummaryGrid.utils';

const DutyLineSummaryGrid = () => {
  const { t } = useTranslation();
  const { hasCostCalculationExternals } = useSelector((state) => state.grants);
  const [yearDateRange] = useRecoilState(yearDateRangeAtom);
  const startDate = toDateString(
    (yearDateRange[0] as Date) ?? startOfYear(new Date())
  );
  const endDate = toDateString(
    (yearDateRange[1] as Date) ?? endOfYear(new Date())
  );

  const { data: dutyLineSummary, isLoading } = useQuery(
    ['dutyLineSummary', startDate, endDate],
    async () => {
      const { data } = await getDutyLinesSummary({
        startDate,
        endDate,
        aggregateOn: 'MONTH'
      });

      const periodsWithNrOfRealizedDuties = data.dutiesRealizationSummaryPeriods
        .map((obj) => ({
          ...obj,
          nrOfRealizedDuties:
            obj.totalNrOfDuties -
            obj.nrOfNotRealizedDuties -
            obj.nrOfPNILInvoicedDuties -
            obj.nrOfPNILNotInvoicedDuties
        }))
        .sort((a, b) => a.periodResponse.year - b.periodResponse.year);

      data.dutiesRealizationSummaryPeriods = periodsWithNrOfRealizedDuties;

      return data;
    }
  );

  const data = useMemo(() => {
    if (!dutyLineSummary?.dutiesRealizationSummaryPeriods) {
      return {
        columns: [],
        rows: []
      };
    }

    const { dutiesRealizationSummaryPeriods } = dutyLineSummary;

    const columns = dutiesRealizationSummaryPeriods.map((d, i) => ({
      field: i.toString(),
      width: 110,
      renderHeader: () => (
        <Box>
          {getLocalizedMonthName(toMonth(d.periodResponse.number - 1))}{' '}
          <Box
            component="span"
            sx={{ fontSize: '10px', color: GRAY_SECONDARY }}
          >
            {d.periodResponse.year}
          </Box>
        </Box>
      )
    }));

    const generateMonthValuesByKey = (key: string) => {
      const result = {};
      for (let i = 0; i < dutiesRealizationSummaryPeriods.length; i++) {
        result[i] = dutiesRealizationSummaryPeriods[i][key];
      }
      return result;
    };

    const rows = [
      {
        id: 0,
        path: [
          t(`adminPortal.realization.dutyLineSummary.nrOfNotRealizedDuties`)
        ],
        ...generateMonthValuesByKey('nrOfNotRealizedDuties')
      },
      {
        id: 1,
        path: [t(`adminPortal.realization.dutyLineSummary.nrOfRealizedDuties`)],
        ...generateMonthValuesByKey('nrOfRealizedDuties')
      },
      {
        id: 2,
        path: [
          t(`adminPortal.realization.dutyLineSummary.nrOfNotRealizedDuties`),
          t(`adminPortal.realization.dutyLineSummary.nrOfRealizableDuties`)
        ],
        ...generateMonthValuesByKey('nrOfRealizableDuties')
      },
      {
        id: 3,
        path: [
          t(`adminPortal.realization.dutyLineSummary.nrOfNotRealizedDuties`),
          t(`adminPortal.realization.dutyLineSummary.nrOfNotRealizableDuties`)
        ],
        ...generateMonthValuesByKey('nrOfNotRealizableDuties')
      },
      {
        id: 4,
        path: [
          t(`adminPortal.realization.dutyLineSummary.nrOfNotRealizedDuties`),
          t(`adminPortal.realization.dutyLineSummary.nrOfNotRealizableDuties`),
          t(`adminPortal.realization.dutyLineSummary.nrOfNotCalculatedDuties`)
        ],
        ...generateMonthValuesByKey('nrOfNotCalculatedDuties')
      },
      {
        id: 5,
        path: [
          t(`adminPortal.realization.dutyLineSummary.nrOfNotRealizedDuties`),
          t(`adminPortal.realization.dutyLineSummary.nrOfNotRealizableDuties`),
          t(`adminPortal.realization.dutyLineSummary.nrOfPendingDuties`)
        ],
        ...generateMonthValuesByKey('nrOfPendingDuties')
      },
      {
        id: 6,
        path: [
          t(`adminPortal.realization.dutyLineSummary.nrOfNotRealizedDuties`),
          t(`adminPortal.realization.dutyLineSummary.nrOfNotRealizableDuties`),
          t(`adminPortal.realization.dutyLineSummary.nrOfBlockedDuties`)
        ],
        ...generateMonthValuesByKey('nrOfBlockedDuties')
      },
      {
        id: 7,
        path: [
          t(`adminPortal.realization.dutyLineSummary.nrOfNotRealizedDuties`),
          t(`adminPortal.realization.dutyLineSummary.nrOfNotRealizableDuties`),
          t(`adminPortal.realization.dutyLineSummary.nrOfRejectedDuties`)
        ],
        ...generateMonthValuesByKey('nrOfRejectedDuties')
      }
    ];
    if (hasCostCalculationExternals) {
      rows.push(
        {
          id: 8,
          path: [
            t(
              `adminPortal.realization.dutyLineSummary.nrOfPNILNotInvoicedDuties`
            )
          ],
          ...generateMonthValuesByKey('nrOfPNILNotInvoicedDuties')
        },
        {
          id: 9,
          path: [
            t(`adminPortal.realization.dutyLineSummary.nrOfPNILInvoicedDuties`)
          ],
          ...generateMonthValuesByKey('nrOfPNILInvoicedDuties')
        }
      );
    }
    rows.push({
      id: 12,
      path: [t(`adminPortal.realization.dutyLineSummary.totalNrOfDuties`)],
      ...generateMonthValuesByKey('totalNrOfDuties')
    });

    return {
      columns,
      rows
    };
  }, [dutyLineSummary]);

  return (
    <Box pt={6}>
      <YearPeriodSelector />
      <StyledTableBoxWrapper>
        <TableCRUDLayout
          columns={data?.columns}
          rows={data?.rows}
          hasTreeData
          getTreeDataPath={(row) => row.path}
          disableColumnReorder
          loading={isLoading}
          disableActions
          groupingColDef={{
            headerName: t('description'),
            width: 250,
            renderCell: (params) => (
              <CustomGridTreeDataGroupingCell
                {...params}
                tooltips={[
                  {
                    groupingKey: t(
                      `adminPortal.realization.dutyLineSummary.nrOfRealizableDuties`
                    ),
                    title: t(
                      'adminPortal.realization.dutyLineSummary.tooltips.nrOfRealizableDuties'
                    )
                  },
                  {
                    groupingKey: t(
                      `adminPortal.realization.dutyLineSummary.nrOfRealizedDuties`
                    ),
                    title: t(
                      'adminPortal.realization.dutyLineSummary.tooltips.nrOfRealizedDuties'
                    )
                  },
                  {
                    groupingKey: t(
                      `adminPortal.realization.dutyLineSummary.nrOfNotRealizableDuties`
                    ),
                    title: t(
                      'adminPortal.realization.dutyLineSummary.tooltips.nrOfNotRealizableDuties'
                    )
                  }
                ]}
              />
            )
          }}
          hideFooter
        />
      </StyledTableBoxWrapper>
    </Box>
  );
};

export default DutyLineSummaryGrid;
